import { ROL_ETIKET, type Rol } from "@oasis/domain";
import { Band, Buton, Etiket, Hata, Modal, alan, etiket as etiketSinifi, girdi } from "@/components/parcalar";
import { cevir } from "@/lib/dil";
import { ekPencereKuyrukCumlesi, ekRozeti, type EkKuyruk } from "@/lib/ek-reject";
import { tarihSaat } from "@/lib/veri";

/**
 * D-072: muayene bekleyen kalemde (sınıf `muayene_bekliyor`) "No record available" (Inspection verification, riser triyajı).
 *
 * Kalemin muayene sonucu başka bir rapordan (takip muayenesi) bekleniyor ve o rapor dosyada yoksa kalite lideri (PM ve sistem
 * yöneticisi her zaman) eksik belgenin yerine gerekçeli bir beyan yazar. Beyanlı kalem barda yeşildir, komponenti artık tutmaz;
 * "Take back…" onu yeniden muayene bekleyenlere alır. Sayım, "tamam" ve "aktarılabilir" SUNUCUDA hesaplanır
 * (muayene.controller.ts `komponentDurumu`); buradaki parçalar yalnız sunucunun döndürdüğü alanları ekrana çevirir.
 *
 * Kalıp kesim-mesafesi.tsx'inki (D-070): saf yardımcılar ve denetimli parçalar; pencere durumu (yazılan gerekçe ve kanıt, meşgul,
 * hata) ve sunucu çağrısı DogrulamaRiser.tsx'tedir (`dene` sayfanın tamamını yeniden okur). Yetki "kayitYokKapi": öteki rollerde
 * düğmeler pasiftir ve ipucunda yetki yazar ("Yetki: Quality Lead").
 *
 * Cümleler `cevir("… {0} …", deger)` ile kurulur; Türkçeleri docs/ceviri/tr-elle.tsv'de `# U-069 (D-072)` başlığı altındadır.
 * Kalem adı, komponent adı, gerekçe ve kanıt referansı veridir, çevrilmez.
 */

/** Kalemin sınıfı (D-016: henüz muayene edilmemiş, takip muayenesinin raporunu bekleyen kalem) ve beyanlı kalemin `durum` değeri. */
const MUAYENE_BEKLIYOR = "muayene_bekliyor";
const KAYIT_YOK_DURUMU = "kayit_yok";

/** Gerekçe ve kanıt kutularının sınırı (sunucunun gövde şemasıyla aynı; kutu sınırı taşırmaz, şemanın cümlesiz reddi hiç görülmez). */
export const KAYIT_YOK_UST_SINIR = 2000;

/** Etkin beyanın damgası (`muayene_kalem.kayit_yok` kolonunun üst düzeyi). `tarih` ISO; tanınmayan rolde `rol` null. */
export interface KayitYokDamgasi { kim: string; rol: Rol | null; tarih: string; gerekce: string; kanit: string | null }

const nesne = (v: unknown): Record<string, unknown> | null => (v !== null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null);
const metin = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);

/**
 * Kalemin etkin damgası; sunucunun okumasıyla aynı kural (`kayitYokOku`): üst düzeyde dolu `kim` ve okunur bir ISO `tarih`. Biri yoksa
 * (kolon boş, yalnız geri alınanlar, ham tablo aktarımı ya da elle yazım) null; hata verilmez. Geri alınanlar (`geriAlinanlar`) okunmaz.
 */
export function kayitYokAktif(x: { kayitYok?: unknown }): KayitYokDamgasi | null {
  const o = nesne(x.kayitYok);
  const kim = metin(o?.kim);
  const tarih = metin(o?.tarih);
  if (!o || !kim || !tarih || Number.isNaN(Date.parse(tarih))) return null;
  const rol = metin(o.rol);
  return {
    kim,
    rol: rol && Object.prototype.hasOwnProperty.call(ROL_ETIKET, rol) ? (rol as Rol) : null,
    tarih,
    gerekce: typeof o.gerekce === "string" ? o.gerekce : "",
    kanit: metin(o.kanit),
  };
}

/**
 * Komponentin muayene bekleyen kalemleri iki listede: `bekleyen` beyansız (grup "Inspection pending", başlık çipi, mavi bant) ve
 * `beyanli` "No record available" yazılmış (grup "Inspection: No record available"). Tanım sunucununkiyle aynıdır (sınıf
 * `muayene_bekliyor`; beyanlı = `durum` "kayit_yok"), bu yüzden `bekleyen.length` sunucunun `sayim.muayeneBekleyen`ine eşittir.
 */
export function muayeneKalemleri<T extends { sinif: string; durum: string }>(kalemler: readonly T[]): { bekleyen: T[]; beyanli: T[] } {
  const muayene = kalemler.filter((x) => x.sinif === MUAYENE_BEKLIYOR);
  return { bekleyen: muayene.filter((x) => x.durum !== KAYIT_YOK_DURUMU), beyanli: muayene.filter((x) => x.durum === KAYIT_YOK_DURUMU) };
}

/** Rolün ekran adı ("Quality Lead"); tanınmayan rolde boş. */
const rolAdi = (rol: Rol | null): string => (rol ? cevir(ROL_ETIKET[rol]) : "");
/** Damganın kişi satırı: "Mustafa Ali Hançar (Quality Lead) · 19/10/2026, 14:05" (rol yoksa parantez yazılmaz). */
export const kayitYokKisiMetni = (d: Pick<KayitYokDamgasi, "kim" | "rol" | "tarih">): string => {
  const rol = rolAdi(d.rol);
  return `${d.kim}${rol ? ` (${rol})` : ""} · ${tarihSaat(d.tarih)}`;
};
/** Damganın gerekçe satırı: "reason: … · evidence: …" (kanıt yoksa ikinci parça yazılmaz). */
export const kayitYokGerekceMetni = (d: Pick<KayitYokDamgasi, "gerekce" | "kanit">): string =>
  `${cevir("reason:")} ${d.gerekce}${d.kanit ? cevir` · evidence: ${d.kanit}` : ""}`;

/**
 * Beyanlı kalemin kartında, kalem gövdesinin altındaki gri damga: başlık ("No record available · inspection report not on file"),
 * gerekçe ve kanıt, kim (rolüyle) ve zaman. Okunur damgası olmayan beyanlı kalemde (yalnız elle yazımla) çizilmez.
 */
export function KayitYokDamgasiKutusu({ damga }: { damga: KayitYokDamgasi | null }) {
  if (!damga) return null;
  return (
    <div className="mt-1 rounded-md border-l-2 border-line-strong bg-surface-2 px-2 py-1 text-sm">
      <span className="font-semibold">{cevir("No record available")}</span>
      <span className="text-ink-3">{cevir(" · inspection report not on file")}</span>
      <div className="text-xs text-ink-2">{kayitYokGerekceMetni(damga)}</div>
      <div className="text-xs text-ink-3">{kayitYokKisiMetni(damga)}</div>
    </div>
  );
}

/** "Inspection pending" kartının sağ sütunu: var olan "inspection pending" çipi ve altında küçük "No record available…" düğmesi. */
export function BekleyenKalemSagi({ onKayitYok }: { onKayitYok: () => void }) {
  return (
    <span className="flex flex-col items-end gap-1">
      <Etiket tone="hold">{cevir("inspection pending")}</Etiket>
      <Buton kucuk islem="kayitYokKapi" onClick={onKayitYok}>{cevir("No record available…")}</Buton>
    </span>
  );
}

/** Beyanlı kartın sağ sütunu: gri "No record available" çipi ve küçük "Take back…" düğmesi. */
export function BeyanliKalemSagi({ onGeriAl }: { onGeriAl: () => void }) {
  return (
    <span className="flex flex-col items-end gap-1">
      <Etiket>{cevir("No record available")}</Etiket>
      <Buton kucuk islem="kayitYokKapi" onClick={onGeriAl}>{cevir("Take back…")}</Buton>
    </span>
  );
}

/** Beyanlı kalemler grubunun başlığı ("Inspection: No record available (1)") ve başlığın altındaki gri not. */
export const kayitYokGrupBasligi = (n: number): string => cevir("Inspection: No record available ({0})", n);
export const kayitYokGrupNotu = (): string =>
  cevir("The inspection result of these Items is not on file; “No record available” was recorded in place of the missing document. They count as green for the bar and do not hold the component. “Take back…” returns an Item to Inspection pending.");

/** "Inspection pending" grubunun mavi bandına eklenen cümle (bandın bugünkü cümlesinden sonra). */
export const kayitYokBantEki = (): string =>
  cevir("If the follow-up inspection report is not on file and will not come, the Quality Lead can record “No record available” on the Item instead.");

/** Sunucunun komponent sayımından bu dosyanın okuduğu iki alan (eski yanıtta yoksa 0 sayılır). */
type MuayeneSayim = { muayeneBekleyen?: number; muayeneKayitYok?: number };

/**
 * Komponent rozetinin D-072 dalı (sarı): beyansız muayene bekleyen kalem varken. Aktarılmış komponentte (yalnız beyan geri alınınca
 * olabilir) "transferred to assembly · re-opened: 1 inspection pending", aktarılmamışta "1 inspection pending · cannot be transferred".
 * Bekleyen yoksa null. Rozetteki yeri `komponentRozeti`dir (D-069 dallarından sonra; aktarılmamış komponentte değişim ve açık rework
 * dallarından sonra).
 */
export function muayeneRozeti(k: { aktarildi: boolean; sayim: MuayeneSayim }): { metin: string; ton: "warn" } | null {
  const n = k.sayim.muayeneBekleyen ?? 0;
  if (n === 0) return null;
  return k.aktarildi
    ? { metin: cevir("transferred to assembly · re-opened: {0} inspection pending", n), ton: "warn" }
    : { metin: cevir("{0} inspection pending · cannot be transferred", n), ton: "warn" };
}

/**
 * Rozetin okuduğu komponent alanları (sunucunun `aktarildi`, `siparisTarihi` ve `sayim`ı; D-069 ve D-072 sayımları eski yanıtta yoksa 0).
 * D-075: `tamam` sunucunun "komponent tamam mı" cevabıdır (`komponentDurumu`); `sayim.gecenBekliyor` Fastener'ın aktarılmayı bekleyen
 * geçen kalemleridir (öteki komponentlerde 0). D-075'ten önceki sunucunun yanıtı `tamam`ı taşır ama `gecenBekliyor`u taşımaz; eski
 * yanıt bu alanın yokluğundan tanınır ve o yanıtta (ya da `tamam` yokken) değişim dalı bugünkü gibidir.
 */
export interface RozetKomponenti {
  aktarildi: boolean;
  siparisTarihi: string | null;
  tamam?: boolean;
  sayim: { degisim: number; bekleyen: number; reworkAcik: number; rework: number; idari: number; ekReject?: number; ekBekleyen?: number; gecenBekliyor?: number } & MuayeneSayim;
}
export type KomponentRozeti = { metin: string; ton: "ok" | "warn" | "bad" };

/**
 * Triyaj sayfasındaki komponent rozeti ve riser kutusundaki noktanın rengi/ipucu (DogrulamaRiser.tsx `rozetBilgi` bunu çağırır).
 * Prototipin rozet dalları birebir: sıra değişim → rework → aktarılabilir.
 * D-069: önce ek REJECT dalları sorulur (hepsi kırmızı): kalemi açılmamış REJECT satırı ya da aktarılmış komponentte bekleyen ek REJECT
 * kalemi varken "transferred to assembly" yeşil yazılmaz; kalem Repair planning'e gönderilince rozet yeşile döner.
 * D-072 (tasarım 3.1.6'nın sırası): beyansız muayene bekleyen kalem varken (sarı, `muayeneRozeti`) aktarılmış komponentte
 * "transferred to assembly" yerine "… re-opened: n inspection pending"; aktarılmamışta değişim kalemlerinin hepsi işlenmişse ve açık
 * rework yoksa yeşil "can be transferred to assembly" / "Replacement completed" yerine "n inspection pending · cannot be transferred"
 * (aktarım düğmesi zaten çizilmez). Değişim sürerken ve açık rework varken bugünkü dallar önce gelir.
 * U-069 inceleme turu (22.10.2026): sayfadan buraya, saf fonksiyon olarak taşındı ki sıra testle sabitlensin; metinler ve dallar aynı.
 * D-075: değişim dalının "bütün değişim kalemleri işlenmiş" hâli (`degisimTamamRozeti`): (a) muayene bekleyen kalem varsa D-072'nin
 * rozeti; (b) komponent tamamsa yeşil "Replacement completed · Items in assembly"; (c) açık rework varsa sarı "Replacement completed ·
 * rework analysis pending (n items)"; (d) Fastener'da aktarılmamış geçen kalem varsa sarı "… passed items not transferred (n items)".
 * Yeşil artık sunucunun tamam cevabına bağlıdır; öteki bütün dallar bugünkü gibidir.
 * D-077: D-075'in (e) dalı (kırmızı "… blocked: no passed items to transfer"; hiç geçen kalemi olmayan Fastener) kalktı: o Fastener artık
 * değişim kalemleri işlenince tamamdır ve (b)'ye düşer; değişim dalının son cevabı yeşildir.
 * `siparis` = sipariş tarihinin eki (" · order: 12/10/2026 (5 g)"); sayfa kendi tarih yardımcılarıyla kurar, tarih yoksa boş.
 */
export function komponentRozeti(k: RozetKomponenti, siparis: string): KomponentRozeti {
  const ek = ekRozeti(k);
  if (ek) return ek;
  const muayene = muayeneRozeti(k);
  if (k.aktarildi) return muayene ?? { metin: cevir("transferred to assembly"), ton: "ok" };
  if (k.sayim.degisim) {
    const b = k.sayim.bekleyen, d = k.sayim.degisim - b;
    if (b === 0) return muayene ?? degisimTamamRozeti(k);
    if (d > 0) return { metin: cevir`Replacement ongoing · ${d}/${k.sayim.degisim} replaced${siparis}`, ton: "warn" };
    if (k.siparisTarihi) return { metin: cevir`awaiting material${siparis}`, ton: "warn" };
    return { metin: cevir("Replacement process · waiting for order / Replacement"), ton: "bad" };
  }
  if (k.sayim.reworkAcik) return { metin: cevir`rework analysis pending (${k.sayim.reworkAcik} items)`, ton: "warn" };
  if (muayene) return muayene;
  // 07-md §7: rozet idari kapanış sayısını da gösterir (rapor olmadan kapanan konu ayrı izlenir).
  const idari = k.sayim.idari ? cevir` · ${k.sayim.idari} Administrative closure` : "";
  if (k.sayim.rework) return { metin: cevir`rework closed${idari} · can be transferred to assembly`, ton: "ok" };
  return { metin: cevir("can be transferred to assembly"), ton: "ok" };
}

/**
 * D-075: değişim kalemlerinin hepsi işlenmiş ve muayene bekleyen kalem yokken rozet (`komponentRozeti`in (b)-(d) dalları). Komponent
 * tamam değilse kalan işi söyler (önce açık rework, sonra geçen kalemler; ikisi birlikteyse rework kapanınca rozet geçen kalemlere
 * geçer). Fastener kuralı burada yeniden yazılmaz: geçen kalem sayısı sunucunun `gecenBekliyor`udur.
 * Eski sunucu (sürüm geri alınınca açık kalan sekme): D-075'ten önceki triyaj yanıtı `tamam`ı zaten taşır, eksik olan `gecenBekliyor`dur.
 * O alan yokken (c) ve (d) seçilmez; bugünkü yeşil döner, yani sayfa yenilenince eski paketin göstereceği rozet (`tamam` yokken de).
 * D-077 (U-076): son cevap yeşildir. D-075'in kırmızı (e) dalı ("… blocked: no passed items to transfer") kalktı: ona yalnız hiç geçen
 * kalemi olmayan Fastener düşüyordu ve o Fastener artık sunucuda tamamdır. Bütün değişim kalemleri işlenmişken komponentin tamam
 * olmamasının öteki nedenleri önceki dallarda söylenir (D-069'un ek REJECT dalları, muayene bekleyen kalem, açık rework, aktarılmamış
 * geçen kalemler); yerine yeni bir savunma dalı yazılmadı (tasarımın kararı).
 */
function degisimTamamRozeti(k: RozetKomponenti): KomponentRozeti {
  const gecen = k.sayim.gecenBekliyor;
  if (k.tamam === false && gecen !== undefined) {
    if (k.sayim.reworkAcik > 0) return { metin: cevir("Replacement completed · rework analysis pending ({0} items)", k.sayim.reworkAcik), ton: "warn" };
    if (gecen > 0) return { metin: cevir("Replacement completed · passed items not transferred ({0} items)", gecen), ton: "warn" };
  }
  return { metin: cevir("Replacement completed · Items in assembly"), ton: "ok" };
}

/** Çubuğun altındaki sayım cümlesine eklenen parça (" · 1 no record available"); beyanlı kalem yoksa boş. */
export const kayitYokCubukAlti = (sayim: MuayeneSayim): string => ((sayim.muayeneKayitYok ?? 0) > 0 ? cevir(" · {0} no record available", sayim.muayeneKayitYok) : "");

/** Düğmelerin üstündeki sarı bant: aktarılmış komponentte beyansız muayene bekleyen kalem varken (beyan geri alındı). */
export const muayeneYenidenAcikCumlesi = (n: number): string =>
  cevir("This component is already transferred to assembly and is open again: {0} Item is waiting for inspection. The transfer and everything recorded so far are kept; the component is complete again when “No record available” is recorded on the Item again.", n);

/** Geri alma penceresinin sarı kutusu, ilk cümle: komponent aktarılmışsa. */
export const kayitYokGeriAktarilmisCumlesi = (komponent: string): string =>
  cevir("{0} is already transferred to assembly; it is open again until “No record available” is recorded on the Item again.", komponent);

/**
 * Geri alma penceresinin kuyruk cümlesi (D-069'un cümleleri): yalnız komponent tamamsa (geri alma onu açar), riser assembly
 * kuyruğundaysa ve kuyruk kaydı damganın zamanından eski değilse (riser beyandan sonra kuyruğa girmiş). Sunucunun koşulu aynıdır:
 * beyandan ÖNCE elle gönderilmiş riserin kuyruğuna dokunulmaz, cümle de çıkmaz. Okunur damga ya da kuyruk zamanı yoksa zaman şartı
 * tutar. Coating ya da Assembly raporu yüklüyse "stays in the assembly queue", değilse "leaves the assembly queue now".
 */
export function kayitYokGeriKuyrukCumlesi(rn: string, k: { tamam: boolean }, kuyruk: EkKuyruk | null | undefined, damga: Pick<KayitYokDamgasi, "tarih"> | null): string | null {
  if (!k.tamam || !kuyruk) return null;
  if (damga && kuyruk.gonderimTs && Date.parse(kuyruk.gonderimTs) < Date.parse(damga.tarih)) return null;
  return ekPencereKuyrukCumlesi(rn, kuyruk);
}

/** Geri alma penceresinin sarı kutusundaki cümleler (koşullu; hiçbiri yoksa kutu çizilmez). */
export function kayitYokGeriNotlari(rn: string, k: { ad: string; aktarildi: boolean; tamam: boolean }, kuyruk: EkKuyruk | null | undefined, damga: Pick<KayitYokDamgasi, "tarih"> | null): string[] {
  return [k.aktarildi ? kayitYokGeriAktarilmisCumlesi(k.ad) : null, kayitYokGeriKuyrukCumlesi(rn, k, kuyruk, damga)].filter((c): c is string => Boolean(c));
}

/** Pencerelerin kalemi: numara ve ad ("#48 Bolt (Locking Bolt) ×6"). */
type PencereKalemi = { no: number; kalem: string };

/**
 * "No record available…" penceresi (dar Modal). Başlığın altında komponentin adı (veri). Gerekçe zorunludur: boşken ya da yalnız
 * boşluk varken kaydet düğmesi pasiftir; kanıt referansı isteğe bağlıdır. Kendi hata kutusu vardır (sayfanın hata bandı pencerenin
 * arkasında kalır); hata olunca pencere sunucunun cümlesiyle açık kalır. Gönderilirken kilitlidir.
 */
export function KayitYokPenceresi({ kalem, komponent, gerekce, kanit, mesgul, hata, onGerekce, onKanit, onKaydet, onKapat }: {
  kalem: PencereKalemi; komponent: string; gerekce: string; kanit: string; mesgul: boolean; hata: string | null;
  onGerekce: (v: string) => void; onKanit: (v: string) => void; onKaydet: () => void | Promise<void>; onKapat: () => void;
}) {
  return (
    <Modal baslik={cevir("No record available: {0}", `#${kalem.no} ${kalem.kalem}`)} onKapat={onKapat} kilitli={mesgul}>
      <p className="text-xs text-ink-3">{komponent}</p>
      <p className="mt-2 text-sm leading-relaxed text-ink-2">{cevir("The inspection result of this Item is expected from another report, and that report is not on file. “No record available” is recorded in place of the missing document: no inspection result is entered, and the Item no longer holds the component.")}</p>
      <div className="mt-3">
        <label className={etiketSinifi}>{cevir("Reason (mandatory)")}</label>
        <textarea className={alan} rows={3} maxLength={KAYIT_YOK_UST_SINIR} aria-label={cevir("Reason (mandatory)")} placeholder={cevir("why there is no inspection record")}
          value={gerekce} disabled={mesgul} onChange={(e) => onGerekce(e.target.value)} />
      </div>
      <div className="mt-3">
        <label className={etiketSinifi}>{cevir("Evidence reference (optional)")}</label>
        <input type="text" className={girdi} maxLength={KAYIT_YOK_UST_SINIR} aria-label={cevir("Evidence reference (optional)")} placeholder={cevir("document and page, e.g. Assembly record p.4")}
          value={kanit} disabled={mesgul} onChange={(e) => onKanit(e.target.value)} />
      </div>
      <p className="mt-3 text-xs text-ink-3">{cevir("Who can enter: the Quality Lead only (PM and System Administrator always). Your name and the time are recorded.")}</p>
      <p className="mt-1 text-xs text-ink-3">{cevir("The declaration is listed in Riser Documentation under “Declarations in place of documents”. The reason and the evidence reference stay on this Item, in the component log and in the audit log.")}</p>
      {hata && <div className="mt-2"><Hata mesaj={hata} /></div>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Buton tur="birincil" islem="kayitYokKapi" mesgul={mesgul} disabled={!gerekce.trim()} onClick={onKaydet}>{cevir("Mark “No record available”")}</Buton>
        <Buton disabled={mesgul} onClick={onKapat}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}

/**
 * "Take back…" penceresi (dar Modal; desen kesim mesafesinin geri alma penceresi). Metin damganın kişisini, rolünü ve zamanını söyler;
 * sarı kutudaki cümleler `notlar`dır (`kayitYokGeriNotlari`). `onGeriAl` pencerenin gösterdiği damganın zamanını alır ve sunucuya
 * gönderir: sayfa bayatsa (başka biri bu arada geri alıp yeniden yazmışsa) sunucu reddeder. Okunur damgası olmayan beyanda (yalnız
 * elle yazımla) metin "—" yazar ve zaman gönderilmez; sunucu geri almayı yine yapar.
 */
export function KayitYokGeriPenceresi({ kalem, damga, notlar, mesgul, hata, onGeriAl, onKapat }: {
  kalem: PencereKalemi; damga: KayitYokDamgasi | null; notlar: readonly string[]; mesgul: boolean; hata: string | null;
  onGeriAl: (tarih: string | undefined) => void | Promise<void>; onKapat: () => void;
}) {
  return (
    <Modal baslik={cevir("Take back “No record available” — #{0} {1}", kalem.no, kalem.kalem)} onKapat={onKapat} kilitli={mesgul}>
      <p className="text-sm leading-relaxed text-ink-2">
        {cevir("“No record available” was recorded by {0} ({1}) · {2}. Taking it back returns the Item to Inspection pending: the component cannot be completed or transferred to assembly until “No record available” is recorded on the Item again. The declaration stays in the Item's history and in the audit log. Your name and the time are recorded.",
          damga?.kim ?? "—", (damga && rolAdi(damga.rol)) || "—", damga ? tarihSaat(damga.tarih) : "—")}
      </p>
      {notlar.length > 0 && (
        <Band tip="not" className="mt-3">{notlar.map((c, i) => <div key={i} className={i ? "mt-1" : undefined}>{c}</div>)}</Band>
      )}
      {hata && <div className="mt-2"><Hata mesaj={hata} /></div>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Buton tur="tehlike" islem="kayitYokKapi" mesgul={mesgul} onClick={() => onGeriAl(damga?.tarih)}>{cevir("Take back “No record available”")}</Buton>
        <Buton disabled={mesgul} onClick={onKapat}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}
